import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';

export interface ConfirmOptions {
    title?: string;
    description?: string;
    confirmText?: string;
    cancelText?: string;
    /** Style the confirm button as a destructive (red) action. */
    destructive?: boolean;
}

type ConfirmFn = (options?: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn>(() => Promise.resolve(false));

/**
 * App-wide promise-based confirmation dialog. Replaces native window.confirm()
 * with a styled, theme-aware AlertDialog. Usage:
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title: 'Delete X?', destructive: true }))) return;
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState<ConfirmOptions>({});
    const resolver = useRef<((value: boolean) => void) | null>(null);

    const confirm = useCallback<ConfirmFn>((opts = {}) => {
        setOptions(opts);
        setOpen(true);
        return new Promise<boolean>((resolve) => {
            resolver.current = resolve;
        });
    }, []);

    const settle = (value: boolean) => {
        setOpen(false);
        resolver.current?.(value);
        resolver.current = null;
    };

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}
            <AlertDialog open={open} onOpenChange={(next) => !next && settle(false)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{options.title ?? 'Are you sure?'}</AlertDialogTitle>
                        {options.description && (
                            <AlertDialogDescription>{options.description}</AlertDialogDescription>
                        )}
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => settle(false)}>
                            {options.cancelText ?? 'Cancel'}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            className={cn(
                                options.destructive &&
                                    'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/40',
                            )}
                            onClick={() => settle(true)}
                        >
                            {options.confirmText ?? 'Confirm'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </ConfirmContext.Provider>
    );
}

export function useConfirm(): ConfirmFn {
    return useContext(ConfirmContext);
}
